import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { MoneyInput } from '@/components/ui/money-input';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { cn } from '@/utils/cn';
import { formatMoney } from '@/utils/format';

export interface CreditsSummary {
  balanceMinor: number;
  currency: string;
  packsMinor: number[];
  purchaseMinMinor: number;
  purchaseMaxMinor: number;
  costPerSegmentMinor: number;
  segmentLength: number;
  paymentsConfigured: boolean;
  testCheckoutAllowed: boolean;
}

interface CheckoutSession {
  id: string;
  status: string;
  creditAmountMinor: number;
  amountMinor: number;
  currency: string;
  checkoutUrl: string | null;
  testMode: boolean;
}

interface PurchaseCreditsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary?: CreditsSummary;
}

const CUSTOM_PACK = 'custom';

const UnityCreditsCaution = ({ message }: { message: string }) => (
  <div className="flex gap-3 rounded-lg border border-warning/30 bg-warning/5 px-4 py-3">
    <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
    <p className="text-body-sm text-fg">{message}</p>
  </div>
);

export const PurchaseCreditsDialog = ({
  open,
  onOpenChange,
  summary: summaryProp,
}: PurchaseCreditsDialogProps) => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const summaryQuery = useQuery({
    queryKey: ['messaging', 'credits'],
    queryFn: () => api.get<CreditsSummary>('/messaging/credits'),
    enabled: open && summaryProp == null,
  });

  const summary = summaryProp ?? summaryQuery.data;

  const defaultPack = summary?.packsMinor[0] ?? summary?.purchaseMinMinor ?? 0;
  const [selectedPack, setSelectedPack] = useState<string>(String(defaultPack));
  const [customAmountMinor, setCustomAmountMinor] = useState<number | null>(defaultPack);

  useEffect(() => {
    if (!open) return;
    setSelectedPack(String(defaultPack));
    setCustomAmountMinor(defaultPack);
  }, [open, defaultPack]);

  const amountMinor = useMemo(() => {
    if (selectedPack === CUSTOM_PACK) return customAmountMinor;
    const parsed = Number(selectedPack);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [customAmountMinor, selectedPack]);

  const customSegmentsEstimate =
    summary && customAmountMinor != null && customAmountMinor > 0
      ? Math.floor(customAmountMinor / summary.costPerSegmentMinor)
      : 0;

  const checkout = useMutation({
    mutationFn: () => {
      if (!summary) {
        throw new Error('summary required');
      }
      if (amountMinor == null || amountMinor <= 0) {
        throw new Error('amount required');
      }
      return api.post<CheckoutSession>('/messaging/credits/checkout-sessions', {
        amountMinor,
        currency: summary.currency,
      });
    },
    onSuccess: async (session) => {
      await queryClient.invalidateQueries({ queryKey: ['messaging', 'credits'] });
      await queryClient.invalidateQueries({ queryKey: ['messaging', 'credits', 'ledger'] });
      await queryClient.invalidateQueries({ queryKey: ['messaging', 'credits', 'checkout-sessions'] });

      if (session.status === 'paid') {
        toast.success(t('credits.purchaseSuccess'));
        onOpenChange(false);
        return;
      }

      if (session.checkoutUrl) {
        window.location.assign(session.checkoutUrl);
        return;
      }

      if (session.status === 'processing' || session.status === 'pending') {
        toast.info(t('credits.purchasePending'));
        onOpenChange(false);
        return;
      }

      toast.error(t('credits.purchaseFailed'));
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const packOptions = summary
    ? [
        ...summary.packsMinor.map((amount) => ({
          value: String(amount),
          label: formatMoney(amount, summary.currency),
          segments: Math.floor(amount / summary.costPerSegmentMinor),
        })),
        { value: CUSTOM_PACK, label: t('credits.customAmount'), segments: null as number | null },
      ]
    : [];

  const amountInvalid =
    !summary ||
    amountMinor == null ||
    amountMinor < summary.purchaseMinMinor ||
    amountMinor > summary.purchaseMaxMinor;

  if (!summary) {
    return (
      <FormDialog
        open={open}
        onOpenChange={onOpenChange}
        title={t('credits.purchaseTitle')}
        description={t('credits.purchaseDescription')}
        size="md"
        submitLabel={t('credits.purchaseConfirm')}
        cancelLabel={t('credits.purchaseCancel')}
        submitting={summaryQuery.isLoading}
        submitDisabled
        onSubmit={() => undefined}
      >
        <div className="space-y-6">
          <UnityCreditsCaution message={t('credits.purchaseUnityOnlyNote')} />
          <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
        </div>
      </FormDialog>
    );
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('credits.purchaseTitle')}
      description={t('credits.purchaseDescription')}
      size="md"
      submitLabel={t('credits.purchaseConfirm')}
      cancelLabel={t('credits.purchaseCancel')}
      submitting={checkout.isPending}
      submitDisabled={amountInvalid}
      onSubmit={() => checkout.mutate()}
    >
      <div className="space-y-6">
        <UnityCreditsCaution message={t('credits.purchaseUnityOnlyNote')} />

        <div>
          <p className="mb-2 text-body-sm font-medium text-fg">{t('credits.chooseAmount')}</p>
          <div
            className="grid grid-cols-2 gap-2 sm:grid-cols-3"
            role="radiogroup"
            aria-label={t('credits.chooseAmount')}
          >
            {packOptions.map((option) => {
              const selected = selectedPack === option.value;
              const segmentLabel =
                option.segments != null
                  ? t('credits.purchasePackSegments', { count: option.segments })
                  : null;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={
                    segmentLabel ? `${option.label}, ${segmentLabel}` : option.label
                  }
                  className={cn(
                    'rounded-md border px-3 py-2.5 text-body-sm font-medium transition-colors',
                    selected
                      ? 'border-brand bg-brand/10 text-brand'
                      : 'border-border bg-surface text-fg hover:bg-surface-secondary',
                  )}
                  onClick={() => setSelectedPack(option.value)}
                >
                  <span className="block">{option.label}</span>
                  {segmentLabel ? (
                    <span
                      className={cn(
                        'mt-0.5 block text-caption font-normal',
                        selected ? 'text-brand/80' : 'text-fg-secondary',
                      )}
                    >
                      {segmentLabel}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        {selectedPack === CUSTOM_PACK ? (
          <div className="space-y-2">
            <FormField
              label={t('credits.customAmountLabel')}
              hint={t('credits.customAmountHelp', {
                min: formatMoney(summary.purchaseMinMinor, summary.currency),
                max: formatMoney(summary.purchaseMaxMinor, summary.currency),
              })}
            >
              {(field) => (
                <MoneyInput
                  id={field.id}
                  value={customAmountMinor}
                  onChange={setCustomAmountMinor}
                  currency={summary.currency}
                  min={summary.purchaseMinMinor}
                  max={summary.purchaseMaxMinor}
                  invalid={amountInvalid}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
            {customAmountMinor != null && customAmountMinor > 0 ? (
              <p className="text-body-sm text-fg-secondary">
                {t('credits.purchaseEstimate', {
                  amount: formatMoney(customAmountMinor, summary.currency),
                  segments: customSegmentsEstimate,
                })}
              </p>
            ) : null}
          </div>
        ) : null}

        {summary.testCheckoutAllowed && !summary.paymentsConfigured ? (
          <p className="text-caption text-fg-secondary">{t('credits.testCheckoutNote')}</p>
        ) : null}
      </div>
    </FormDialog>
  );
};
